import { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
} from "@mui/material";

import InputWithSlider from "./components/ResourceInputSlider";
import FieldLabel from "../components/FieldLabel";
import { INK_MUTED } from "../../../theme/tokens";

const FIELD_STACK = {
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
};

const GPUResourceForm = ({
  value,
  errors,
  onChange,
  disabled,
  isSubmitJob,
  accessibleGPUs,
  effectiveLimits,
  runtimeField,
}) => {
  const clampSliderMax = (limit) => Math.max(1, limit ?? 1);

  useEffect(() => {
    // Switch to a default GPU product if the current one becomes inaccessible or unavailable.
    const nextGpuProduct = accessibleGPUs[0]?.type ?? "";
    const hasAccessibleSelection = accessibleGPUs.some(
      (gpu) => gpu.type === value.gpuProduct,
    );

    if (value.gpuProduct !== nextGpuProduct && !hasAccessibleSelection) {
      onChange({ gpuProduct: nextGpuProduct });
    }
  }, [accessibleGPUs, onChange, value.gpuProduct]);

  const handleFieldChange = (field) => (e) => {
    onChange({ [field]: e.target.value });
  };

  const handleGpuProductChange = (e) => {
    onChange({
      gpuProduct: e.target.value,
      nodeCount: 1,
      gpuCount: 1,
      memory: 32,
      burstBufferEnabled: false,
    });
  };

  const formatGpuLabel = (gpu) =>
    gpu ? `${gpu.type}: ${gpu.available} available` : "";

  const selectedGpu = accessibleGPUs.find(
    (gpu) => gpu.type === value.gpuProduct,
  );

  return (
    <Box sx={FIELD_STACK}>
      {runtimeField}
      {effectiveLimits.has_burst_buffer && (
        <FormControl fullWidth disabled={disabled} sx={{ py: 1 }}>
          <FieldLabel htmlFor="burst-buffer-select">
            Local Burst Buffer
          </FieldLabel>
          <Select
            id="burst-buffer-select"
            value={value.burstBufferEnabled}
            onChange={handleFieldChange("burstBufferEnabled")}
          >
            <MenuItem value={false}>Disabled</MenuItem>
            <MenuItem value={true}>Enabled</MenuItem>
          </Select>
          <FormHelperText>
            Fast local SSD on each worker. Useful for heavy I/O.
          </FormHelperText>
        </FormControl>
      )}

      <InputWithSlider
        id="node-count"
        label="Workers"
        description="Number of workers in the experiment."
        value={value.nodeCount}
        onChange={handleFieldChange("nodeCount")}
        helperText={errors.nodeCount || ""}
        error={!!errors.nodeCount}
        disabled={disabled}
        minInput={1}
        maxInput={clampSliderMax(effectiveLimits.max_workers)}
      />

      <FormControl
        fullWidth
        required
        error={!!errors.gpuProduct}
        sx={{ py: 1 }}
      >
        <FieldLabel htmlFor="gpu-product-select">GPU Product</FieldLabel>
        <Select
          id="gpu-product-select"
          value={value.gpuProduct}
          onChange={handleGpuProductChange}
          disabled={disabled || accessibleGPUs.length === 0}
          displayEmpty
          renderValue={() =>
            selectedGpu ? (
              formatGpuLabel(selectedGpu)
            ) : accessibleGPUs.length === 0 ? (
              "N/A"
            ) : (
              <Box component="span" sx={{ color: INK_MUTED }}>
                Select a GPU product
              </Box>
            )
          }
        >
          {accessibleGPUs.length === 0 && (
            <MenuItem value="" disabled>
              N/A
            </MenuItem>
          )}
          {accessibleGPUs.map((gpu) => (
            <MenuItem key={gpu.type} value={gpu.type}>
              {formatGpuLabel(gpu)}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText
          sx={errors.gpuProduct ? { color: "error.main" } : undefined}
        >
          {errors.gpuProduct || "Select a GPU product for your experiment."}
        </FormHelperText>
      </FormControl>

      {isSubmitJob &&
        value.gpuProduct &&
        (selectedGpu?.available ?? 0) === 0 && (
          <Alert severity="warning">
            No {value.gpuProduct} GPUs are currently free. Your job will be
            queued until resources become available.
          </Alert>
        )}

      <InputWithSlider
        id="gpu-count"
        label="GPUs per worker"
        description="Number of GPUs per worker."
        value={value.gpuCount}
        onChange={handleFieldChange("gpuCount")}
        helperText={errors.gpuCount || ""}
        error={!!errors.gpuCount}
        disabled={disabled}
        minInput={1}
        maxInput={clampSliderMax(effectiveLimits.max_gpus)}
      />

      <InputWithSlider
        id="gpu-memory-size"
        label="Memory per worker (Gi)"
        description="The memory size per worker (Gi)."
        value={value.memory}
        onChange={handleFieldChange("memory")}
        helperText={errors.memory || ""}
        error={!!errors.memory}
        disabled={disabled}
        minInput={1}
        maxInput={clampSliderMax(effectiveLimits.max_mem_gi)}
      />
    </Box>
  );
};

GPUResourceForm.propTypes = {
  value: PropTypes.shape({
    burstBufferEnabled: PropTypes.bool.isRequired,
    gpuCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    gpuProduct: PropTypes.string.isRequired,
    memory: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    nodeCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
  }).isRequired,
  errors: PropTypes.shape({
    gpuCount: PropTypes.string,
    gpuProduct: PropTypes.string,
    memory: PropTypes.string,
    nodeCount: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitJob: PropTypes.bool,
  accessibleGPUs: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      available: PropTypes.number.isRequired,
    }),
  ).isRequired,
  effectiveLimits: PropTypes.shape({
    has_burst_buffer: PropTypes.bool.isRequired,
    max_workers: PropTypes.number,
    max_cpus: PropTypes.number,
    max_gpus: PropTypes.number,
    max_mem_gi: PropTypes.number,
  }).isRequired,
  // Optional leading field, usually the page's "Runtime" slider.
  runtimeField: PropTypes.node,
};

export default GPUResourceForm;
